import React, { useMemo, useRef, useCallback, useEffect, useState } from 'react';
import classNames from 'classnames';
import { useTranslations } from '@components/context';
import Button from '@components/legacy/core/Button';
import ModalComponent from '../ModalComponent';
import { createTemporaryStepId, sameStepNameConflict } from '@components/RecruitmentFlowEditor/FlowForm/helper';
import PickerPanel, { PICKER_PANEL_PLACEMENT } from '@components/core/PickerPanel';
import SelectDropdown from '@components/core/SelectDropdown';
import { normalizeRecruitmentStepTypes } from './helper';

const EMPTY_I18N = {};

const StepForm = ({
    stepId,
    className,
    onClose = () => { },
    anchorRef,
    positionBottom,
    metadata = {},
    name: nameProp = '',
    type: typeProp = '',
    i18n: i18nProp,
    presets = [],
    onPresetSelect = () => { },
    checkStepExistence = () => false,
    onSubmit: submitStep = () => { },
}) => {
    const { t } = useTranslations();
    const formRef = useRef(null);
    const i18n = i18nProp ?? EMPTY_I18N;
    const [name, setName] = useState(nameProp || '');
    const [type, setType] = useState(typeProp || '');
    const [showCreateStepForm, setShowCreateStepForm] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [i18nData, setI18nData] = useState(() => ({ ...i18n }));
    const [errors, setErrors] = useState([]);

    const stepFormMode = stepId != null ? 'update' : 'create';
    const isUpdatingStep = stepFormMode === 'update';

    const { step_types = {}, languages = [], can_create_recruitment_flow_steps = false } = metadata;

    const showPresetPicker = stepFormMode === 'create' && !showCreateStepForm;
    const showCreateOrEditFields = showCreateStepForm || isUpdatingStep;

    useEffect(() => {
        if (!isUpdatingStep) {
            return;
        }
        setI18nData({ ...i18n });
    }, [i18n, isUpdatingStep]);

    const stepTypesOptions = useMemo(() => normalizeRecruitmentStepTypes(step_types, t), [step_types, t]);

    const updateTranslation = useCallback((code, value) => {
        setI18nData((prev) => ({ ...prev, [code]: value }));
    }, []);

    const handleCreateNewStepClick = useCallback(() => {
        if (!can_create_recruitment_flow_steps) {
            return;
        }
        setShowCreateStepForm(true);
    }, [can_create_recruitment_flow_steps]);

    // The placeholder row of the legacy option list is the field's own placeholder now.
    const statusOptions = useMemo(
        () => stepTypesOptions
            .filter((option) => option.value !== '')
            .map((option) => ({ value: option.value, label: option.name, disabled: option.disabled })),
        [stepTypesOptions],
    );

    const handleStatusChange = useCallback((selectedValue) => {
        setType(selectedValue ?? '');

        if (isUpdatingStep || !selectedValue) {
            return;
        }

        const selectedOption = stepTypesOptions.find((option) => option.value === selectedValue);
        if (!selectedOption?.name) {
            return;
        }

        setName((currentName) => {
            if (String(currentName || '').trim()) {
                return currentName;
            }
            return selectedOption.name;
        });
    }, [isUpdatingStep, stepTypesOptions]);

    const validateStepForm = useCallback(
        (stepName, stepType) => {
            const invalids = [];
            if (!String(stepName || '').trim()) {
                invalids.push(t('invalid_step_name'));
            }
            if (!stepType) {
                invalids.push(t('invalid_step_status'));
            }
            return invalids;
        },
        [t],
    );

    const onSubmit = (e) => {
        e.preventDefault();
        // Creating a brand-new step requires the create permission; editing an existing step does not.
        if (!isUpdatingStep && !can_create_recruitment_flow_steps) {
            return;
        }
        const stepErrors = validateStepForm(name, type);
        setErrors(stepErrors);
        if (stepErrors.length) return;

        const flow_step = {
            i18n: i18nData,
            name,
            type,
            default_name: name,
            id: isUpdatingStep ? stepId : createTemporaryStepId(),
        };
        const duplicateName =
            presets.some((preset) => sameStepNameConflict(preset, flow_step)) || checkStepExistence(flow_step);
        if (duplicateName) {
            setErrors([...stepErrors, t('Step already exists')]);
            return;
        }
        submitStep(flow_step);
    }

    const presetItems = useMemo(
        () => presets.map((preset) => ({
            id: preset.id,
            label: preset.name,
            disabled: preset.isAlreadyPresent,
            className: 'preset-item',
        })),
        [presets],
    );

    // Per-language name overrides are entered in their own inputs; the default-language
    // base name has its own required field above them. (COR-1440)
    const translationLanguages = useMemo(
        () => languages.filter((language) => language.code !== 'default'),
        [languages],
    );

    const createOrEditForm = (
        <form
            id="add-step-form"
            onSubmit={onSubmit}
            className={classNames('create-step-form', 'text-left', {
                'is-step-editing': isUpdatingStep,
            })}
        >
            <div className="overlay-main no-vertical-padding">
                <div className="form-group">
                    <label htmlFor="status">{t('Status')}</label>
                    <SelectDropdown
                        id="step-status"
                        name="status"
                        className="step-status step-status-field"
                        options={statusOptions}
                        value={type || null}
                        placeholder={t('Status')}
                        disabled={isUpdatingStep}
                        searchable={false}
                        onChange={handleStatusChange}
                    />
                </div>
                <div className="form-group">
                    <label htmlFor="name" data-required>
                        {t('Name')}
                    </label>
                    <input
                        name="name"
                        type="text"
                        className="form-control step-name"
                        data-lang="default"
                        placeholder={t('Name')}
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        autoFocus
                    />
                </div>
                {translationLanguages.length > 0 ? (
                    <div className="form-group">
                        <label>{t('Translations')}</label>
                        <div className="step-translations">
                            {translationLanguages.map((language) => (
                                <div className="step-translation" key={language.code}>
                                    <label className="step-translation-lang">{language.name}</label>
                                    <input
                                        type="text"
                                        className="form-control step-name-translation"
                                        data-lang={language.code}
                                        value={i18nData[language.code] || ''}
                                        onChange={(e) => updateTranslation(language.code, e.target.value)}
                                    />
                                </div>
                            ))}
                        </div>
                    </div>
                ) : null}

                <div className="form-group">
                    <ul className="step-invalids">
                        {errors.map((error, index) => (
                            <li key={`${index}-${error}`}>{error}</li>
                        ))}
                    </ul>
                </div>
            </div>
            <div className="overlay-footer">
                <Button
                    type="link"
                    isLink
                    className="overlay-cancel overlay-cancel-link-show step-form-close"
                    title={t('Close')}
                />
                <Button
                    type="primary"
                    className="submit-step-form"
                    title={t(isUpdatingStep ? 'Apply changes' : 'Create new step')}
                    onClick={onSubmit}
                />
            </div>
        </form>
    );

    return (
        <>
            {showPresetPicker ? (
                <PickerPanel
                    ref={formRef}
                    className={classNames('step-form', { 'step-form-below': positionBottom }, className)}
                    placement={positionBottom ? PICKER_PANEL_PLACEMENT.BOTTOM : PICKER_PANEL_PLACEMENT.TOP}
                    items={presetItems}
                    listClassName="preset-list"
                    searchPlaceholder={t('Search step')}
                    query={searchTerm}
                    onQueryChange={setSearchTerm}
                    onSelect={(item) => onPresetSelect(item.id)}
                    onDismiss={onClose}
                    anchorRef={anchorRef}
                    footer={can_create_recruitment_flow_steps ? (
                        <Button
                            type="link"
                            className="create-step-btn"
                            title={t('Create new step')}
                            icon="icon-svg-plus"
                            onClick={handleCreateNewStepClick}
                        />
                    ) : null}
                />
            ) : null}

            {showCreateOrEditFields ? (
                <ModalComponent show={showCreateOrEditFields} closeModal={onClose} id="step-form-modal">
                    <div className="overlay overlay-small step-form-modal">
                        <div className="overlay-header">
                            <h3>{t(isUpdatingStep ? 'Update step' : 'Create new step')}</h3>
                            <a
                                className="overlay-cancel overlay-cancel-mark"
                                aria-label={t('Close')}
                            />
                        </div>
                        {createOrEditForm}
                    </div>
                </ModalComponent>
            ) : null}
        </>
    );
};

export default StepForm;
